'use client'

import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  UseFloatingReturn,
  useInteractions,
  useListNavigation,
  useRole,
  useTypeahead
} from '@floating-ui/react'
import {
  FC,
  MutableRefObject,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import { UseInteractionsReturn } from '../../types'
import { cn } from '../../utils/cn'
import { createRequiredContext } from '../../utils/create-required-context'

interface SelectContextValue extends UseFloatingReturn, UseInteractionsReturn {
  activeIndex: number | null
  selectedIndex: number | null
  handleSelect: (index: number | null) => void
  value: string
  isOpen: boolean
  elementsRef: MutableRefObject<Array<HTMLElement | null>>
  labelsRef: MutableRefObject<Array<string | null>>
}

const [useSelectContext, SelectContextProvider] =
  createRequiredContext<SelectContextValue>('Select')

interface SelectProps {
  value: string
  onChange(value: string): void
  className?: string
  placement?: Placement
  typeaheadEnabled?: boolean
}

const Select: FC<PropsWithChildren<SelectProps>> = ({
  children,
  value,
  onChange,
  className,
  placement = 'bottom',
  typeaheadEnabled = true
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)

  const floatingData = useFloating({
    placement,
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [flip(), offset(4), shift({ padding: 4 })]
  })

  const { context } = floatingData

  const elementsRef = useRef<Array<HTMLElement | null>>([])
  const labelsRef = useRef<Array<string | null>>([])

  useEffect(() => {
    if (labelsRef.current.length > 0) {
      // Detect current selected index after FloatingList set labelsRef.current
      // from the list of all options
      const initialIndex = labelsRef.current.findIndex(label => label === value)

      setSelectedIndex(initialIndex === -1 ? null : initialIndex)
    }
  }, [labelsRef.current.length, value])

  const handleSelect = useCallback(
    (index: number | null) => {
      setSelectedIndex(index)
      setIsOpen(false)
      if (index !== null) {
        onChange(labelsRef.current[index] as string)
      }
    },
    [onChange]
  )

  function handleTypeaheadMatch(index: number | null) {
    if (isOpen) {
      setActiveIndex(index)
    } else {
      handleSelect(index)
    }
  }

  const listNav = useListNavigation(context, {
    listRef: elementsRef,
    activeIndex,
    selectedIndex,
    onNavigate: setActiveIndex
  })

  const typeahead = useTypeahead(context, {
    listRef: labelsRef,
    activeIndex,
    selectedIndex,
    onMatch: handleTypeaheadMatch,
    enabled: typeaheadEnabled
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'listbox' })

  const interactions = useInteractions([
    listNav,
    typeahead,
    click,
    dismiss,
    role
  ])

  const selectContext = useMemo(
    () => ({
      activeIndex,
      selectedIndex,
      handleSelect,
      ...floatingData,
      ...interactions,
      elementsRef,
      labelsRef,
      value,
      isOpen
    }),
    [
      activeIndex,
      selectedIndex,
      interactions,
      handleSelect,
      isOpen,
      value,
      floatingData,
      elementsRef,
      labelsRef
    ]
  )

  return (
    <SelectContextProvider value={selectContext}>
      <div className={cn('relative', className)}>{children}</div>
    </SelectContextProvider>
  )
}

export { Select, useSelectContext }
